import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";

export async function createPayPlanAssignment({ locationId, input, userId, planId }) {
	const query = gql`
		mutation createPayPlanAssignment($locationId: String, $input: CreatePayPlanAssignmentInput, $userId: [String], $planId: String) {
			createPayPlanAssignment(locationId: $locationId, input: $input, userId: $userId, planId: $planId) {
				planId
				userId
				payPlanAssignment {
					_id
					acknowledgementDate
					assignmentDate
					effectiveDate
					planId {
						_id
						name
					}
				}
				users {
					upcomingPayPlanAssignment {
						acknowledgementDate
						assignmentDate
						effectiveDate
						planId {
							_id
							name
						}
						position
						packageId
						acknowledgementDocumentLink
						assignedBy
						acknowledgementStatus {
							status
							statusDate
						}
					}
					_id
					locationId
					employeeId
					fullName
					position
					communications {
						acceptsTxt
						communicationType
						usage
						value
					}
					currentPayPlanAssignment {
						_id
						acknowledgementDate
						acknowledgementStatus {
							status
							statusDate
						}
						acknowledgementDocumentLink
						assignedBy
						assignmentDate
						effectiveDate
						planId {
							_id
							name
							position
						}
						position
						packageId
					}
					payPlanAssignmentHistory {
						_id
						acknowledgementDate
						acknowledgementStatus {
							status
							statusDate
						}
						acknowledgementDocumentLink
						assignedBy
						assignmentDate
						effectiveDate
						planId {
							_id
							name
							position
						}
						position
						packageId
					}
					supervisor {
						_id
						fullName
					}
					preferences {
						landingLocation
						landingPage
					}
					hireDate
				}
			}
		}
	`;

	const variables = {
		locationId,
		input,
		userId,
		planId,
	};

	try {
		return await apiInstance.post("/graphql", { query, variables });
	} catch (err) {
		return null;
	}
}

export async function createPayPlanAssignmentGroupPayPlan({locationId, input, userIds, planId}) {
	const query = gql`
		mutation createPayPlanAssignment($locationId: String, $input: CreatePayPlanAssignmentInput, $userId: [String], $planId: String) {
			createPayPlanAssignment(locationId: $locationId, input: $input, userId: $userId, planId: $planId) {
				payPlanAssignment {
					_id
					acknowledgementDate
					assignmentDate
					effectiveDate
					planId {
						_id
						name
					}
				}
				users {
					upcomingPayPlanAssignment {
						acknowledgementDate
						assignmentDate
						planId {
							_id
							name
						}
					}
					_id
				}
			}
		}
	`;

	const variables = {
		locationId,
		input,
		planId,
		userId: userIds,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response;
	} catch (err) {
		throw new Error(err);
	}
}

export async function removePayPlanAssignmentInUser(locationId, userId, packageId) {
	const query = gql`
		mutation removeUserPayPlanAssignment($locationId: String!, $userId: String!, $packageId: String!) {
			removeUserPayPlanAssignment(locationId: $locationId, userId: $userId, packageId: $packageId) {
				_id
				username
				locationId
				employeeId
				fullName
				position
				communications {
					acceptsTxt
					communicationType
					usage
					value
				}
				upcomingPayPlanAssignment {
					_id
					acknowledgementDate
					acknowledgementStatus {
						status
						statusDate
					}
					acknowledgementDocumentLink
					assignedBy
					assignmentDate
					planId {
						_id
						name
						position
					}
					effectiveDate
					position
					packageId
				}
				currentPayPlanAssignment {
					_id
					acknowledgementDate
					acknowledgementStatus {
						status
						statusDate
					}
					acknowledgementDocumentLink
					assignedBy
					assignmentDate
					effectiveDate
					planId {
						_id
						name
						position
					}
					position
					packageId
				}
				payPlanAssignmentHistory {
					_id
					acknowledgementDate
					acknowledgementStatus {
						status
						statusDate
					}
					acknowledgementDocumentLink
					assignedBy
					assignmentDate
					effectiveDate
					planId {
						_id
						name
						position
					}
					position
					packageId
				}
				supervisor {
					_id
					fullName
				}
				preferences {
					landingLocation
					landingPage
				}
				hireDate
			}
		}
	`;

	const variables = {
		locationId,
		userId,
		packageId,
	};

	try {
		return await apiInstance.post("/graphql", { query, variables });
	} catch (err) {
		return null;
	}
}
export async function resendSignRequestEmailMutation(locationId, packageId) {
	const query = gql`
		mutation resendSignRequestEmail($locationId: String!, $packageId: String!) {
			resendSignRequestEmail(locationId: $locationId, packageId: $packageId)
		}
	`;

	const variables = {
		locationId,
		packageId,
	};

	try {
		return await apiInstance.post("/graphql", { query, variables });
	} catch (err) {
		errorLogger(err, {
			function: "resendSignRequestEmailMutation",
			message: "Failed to post resendSignRequestEmailMutation",
			variables,
		});
		return null;
	}
}

export async function updatePayPlanAssignmentInUser(locationId, userId, payPlanAssignmentId) {
	const query = gql`
		mutation updatePayPlanAssignmentInUser($locationId: String!, $userId: String!, $payPlanAssignmentId: String!) {
			updatePayPlanAssignmentInUser(locationId: $locationId, payPlanAssignmentId: $payPlanAssignmentId, userId: $userId)
		}
	`;

	const variables = {
		locationId,
		userId,
		payPlanAssignmentId,
	};

	try {
		return await apiInstance.post("/graphql", { query, variables });
	} catch (err) {
		return null;
	}
}

export async function updatePayPlanAssignment(locationId, packageId, status, user, payPlan, effectiveDate) {
	const query = gql`
		mutation updatePayPlanAssignment(
			$locationId: String
			$packageId: String
			$status: String
			$user: UserInput
			$payPlan: GenericPayPlanInput
			$effectiveDate: AWSDateTime
		) {
			updatePayPlanAssignment(
				locationId: $locationId
				packageId: $packageId
				status: $status
				user: $user
				payPlan: $payPlan
				effectiveDate: $effectiveDate
			) {
				_id
				position
				planId {
					_id
					name
				}
				assignmentDate
				assignedBy
				effectiveDate
				acknowledgementDate
				packageId
			}
		}
	`;

	const variables = {
		locationId,
		packageId,
		status,
		user,
		payPlan,
		effectiveDate,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response?.data?.data?.updatePayPlanAssignment;
	} catch (err) {
		throw new Error(err);
	}
}
